import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Leaf, Store, Sunrise } from 'lucide-react';

import AllergenNotice from '../../components/public/AllergenNotice';
import AwardBadges from '../../components/public/AwardBadges';
import DonutGrid from '../../components/public/DonutGrid';
import PublicLayout from '../../components/public/PublicLayout';
import SmartImage from '../../components/public/SmartImage';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  business,
  cafePartners,
  cateringPackages,
  coreMenu,
  heroImage,
  wholesaleProof,
  wholesaleSteps,
} from '../../content/siteContent.mjs';

const PROOF_ICONS = { award: Award, sunrise: Sunrise, store: Store, leaf: Leaf };

const Home = () => (
  <PublicLayout testId="public-home">
    <section className="pt-16 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-amber-50 to-white">
      <div className="max-w-5xl mx-auto text-center">
        <p className="mb-4 text-sm font-semibold uppercase tracking-widest text-amber-600">
          Wholesale bakery · {business.baseTown}
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
          Wholesale donuts for
          <br />
          <span className="text-amber-600">Westchester counters</span>
        </h1>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8 leading-relaxed">
          Award-winning donuts made to order overnight in {business.baseTown} and delivered fresh for opening
          to cafés, restaurants and offices across {business.region} and {business.nearby}. Standing orders
          close {business.leadTime} before delivery — no shortcuts, no day-old trays.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button asChild size="lg" className="bg-gray-900 text-white hover:bg-gray-800 text-lg px-8 py-6">
            <Link to="/wholesale#inquiry" data-testid="hero-wholesale-cta">
              Start a wholesale inquiry
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </Button>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="border-gray-900 text-gray-900 hover:bg-gray-50 text-lg px-8 py-6"
          >
            <Link to="/menu" data-testid="hero-menu-cta">See the menu</Link>
          </Button>
        </div>
        <div className="mt-10 flex justify-center">
          <AwardBadges compact />
        </div>
      </div>

      <div className="max-w-5xl mx-auto mt-14">
        <SmartImage
          image={heroImage}
          priority
          sizes="(max-width: 1024px) 100vw, 1024px"
          className="h-[260px] sm:h-[380px] w-full rounded-2xl object-cover shadow-2xl"
        />
      </div>
    </section>

    <section className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Why cafés stock us</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            We supply {cafePartners.length} independent businesses from Peekskill to Greenwich — here is what
            they are buying.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {wholesaleProof.map((proof) => {
            const Icon = PROOF_ICONS[proof.icon];
            return (
              <Card
                key={proof.title}
                className="p-8 text-center hover:shadow-xl transition-shadow duration-300"
                data-testid={`proof-${proof.icon}`}
              >
                <div className="flex justify-center mb-6">
                  <div className="bg-amber-100 p-4 rounded-full">
                    <Icon size={28} className="text-amber-600" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{proof.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{proof.body}</p>
              </Card>
            );
          })}
        </div>
      </div>
    </section>

    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-amber-50/60">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our signature selection</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Brioche and buttermilk doughs made from scratch, jams cooked in-house — plus a seasonal list that
            changes through the year.
          </p>
        </div>
        <DonutGrid items={coreMenu} testIdPrefix="home-donut" />
        <div className="mt-12 text-center">
          <Button
            asChild
            size="lg"
            variant="outline"
            className="border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white"
          >
            <Link to="/menu" data-testid="home-see-full-menu">
              See the full menu and seasonal specials
            </Link>
          </Button>
        </div>
      </div>
    </section>

    <section className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">How wholesale works</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Four steps from first inquiry to donuts on your counter.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {wholesaleSteps.map((step, i) => (
            <Card key={step.title} className="p-8 hover:shadow-xl transition-shadow duration-300">
              <span className="mb-5 inline-flex h-10 w-10 items-center justify-center rounded-full bg-gray-900 text-base font-bold text-white">
                {i + 1}
              </span>
              <h3 className="text-lg font-bold text-gray-900 mb-3">{step.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{step.body}</p>
            </Card>
          ))}
        </div>
        <div className="mt-12 text-center">
          <Button asChild size="lg" className="bg-gray-900 text-white hover:bg-gray-800 px-8">
            <Link to="/wholesale#inquiry">Tell us what your counter needs</Link>
          </Button>
        </div>
      </div>
    </section>

    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-5xl mx-auto text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Find us on the counter</h2>
        <p className="text-lg text-gray-600 mb-10">
          Not a business? Our donuts are on sale at these independent cafés and roasters.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          {cafePartners.slice(0, 9).map((partner) => (
            <span
              key={partner.name}
              className="rounded-full border border-amber-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm"
            >
              {partner.name} <span className="text-gray-400">· {partner.location}</span>
            </span>
          ))}
        </div>
        <div className="mt-10">
          <Link
            to="/where-to-find-us"
            className="inline-flex items-center gap-2 text-base font-semibold text-amber-700 hover:text-amber-800"
            data-testid="home-see-all-partners"
          >
            See all {cafePartners.length} places to find us
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>

    <section className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Catering for offices and events</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            One-off order rather than a standing one? Three packages, delivery in Westchester included.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {cateringPackages.map((pkg) => (
            <Card key={pkg.id} className="hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                <p className="text-sm text-gray-600 mb-4">{pkg.description}</p>
                <p className="text-sm text-gray-500 mb-1">
                  <span className="font-semibold">Serves:</span> {pkg.servings}
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  <span className="font-semibold">Includes:</span> {pkg.includes}
                </p>
                <p className="text-2xl font-bold text-amber-600 mb-6">{pkg.price}</p>
                <Button asChild className="w-full bg-gray-900 text-white hover:bg-gray-800">
                  <Link to="/catering#quote" data-testid={`home-catering-${pkg.id}`}>Request a quote</Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>

    <AllergenNotice />

    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-5">
          Ready to put MAD Donuts on your counter?
        </h2>
        <p className="text-lg text-gray-300 mb-9">
          Send your town and rough daily volume — we reply within one business day with what we can supply
          and when.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button asChild size="lg" className="bg-amber-500 text-gray-900 hover:bg-amber-400 text-lg px-8 py-6">
            <Link to="/wholesale#inquiry" data-testid="home-footer-cta">Start a wholesale inquiry</Link>
          </Button>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="border-white text-white hover:bg-white hover:text-gray-900 text-lg px-8 py-6"
          >
            <Link to="/contact">Contact the bakery</Link>
          </Button>
        </div>
      </div>
    </section>
  </PublicLayout>
);

export default Home;
